"use client";

import { ReactNode, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";
import * as Sentry from "@sentry/react";

import { CurrentAccountIdProvider } from "@zapier/identity";
import { UniversalLayout } from "@zapier/universal-layout";
import type { EventNameType } from "@zapier/avro-events";
import { Spinner } from "@zapier/design-system";

import {
  FullStory,
  TrackRouting,
  initializeDatadogRum,
  initializeSentry,
  nextJsReportWebVitals,
} from "../observability";
import { isDatadog } from "../utils/isDatadog";
import emitEvent from "../utils/emitEvent";
import { SPLIT_FLAGS } from "../constants/splitFeatureFlags";

interface Props {
  children: ReactNode;
}

const shouldInitSentry = !isDatadog();
if (shouldInitSentry) {
  initializeSentry({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || "",
    commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA || "",
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV || "",
  });
}

// Type for current user data
type CurrentUser = {
  id: string | null;
  identity: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  isLoggedIn: boolean;
  switches: { [key: string]: boolean };
  currentAccountId: string | null;
};

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
initializeDatadogRum();
export const reportWebVitals = nextJsReportWebVitals;

function AppLayout({ children }: Props) {
  const pathname = usePathname();
  const isPopupChat = pathname?.startsWith("/app/popup-chat");
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // Extract the main section from pathname (e.g., /app/get-help -> get-help)
  const mainContentId = pathname?.match(/^\/app\/([^/]+)/)?.[1] || "support";

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(
          "/api/supportfrontend/v1/proxy/current-user",
          {
            credentials: "include",
          },
        );

        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data.currentUser);
        }
      } catch (error) {
        console.error("Failed to fetch current user:", error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Create Split SDK config with user ID
  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: process.env.NEXT_PUBLIC_SPLIT_API_KEY || "localhost",
      key: currentUser?.id || "anonymous", // Use actual customuser_id
    },
    // Mock features for localhost development
    features: process.env.NEXT_PUBLIC_SPLIT_API_KEY
      ? undefined
      : {
          // These only work when authorizationKey is "localhost"
          [SPLIT_FLAGS.AI_CONTACT_FORM]:
            process.env.NEXT_PUBLIC_SPLIT_MOCK_AI_CONTACT_FORM || "off",
          [SPLIT_FLAGS.SUPPORT_COPILOT]:
            process.env.NEXT_PUBLIC_SPLIT_MOCK_SUPPORT_COPILOT || "off",
        },
  };

  // Map user data for FullStory
  const user = currentUser
    ? {
        id: currentUser.id || "", // FullStory requires an id
        fullName: currentUser.fullName,
        email: currentUser.email || "",
      }
    : undefined;

  // Show loading state while fetching user
  if (isLoadingUser) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  // For all other routes, use the standard UniversalLayout
  return (
    <CurrentAccountIdProvider>
      <TrackRouting>
        <SplitFactoryProvider
          config={sdkConfig}
          key={currentUser?.id || "anonymous"} // Re-initialize Split when user changes
        >
          <UniversalLayout
            currentUrl={pathname || ""}
            mainContentId={mainContentId}
            mode={isPopupChat ? "navigation" : "in-app"}
            {...(isPopupChat && {
              renderAlertBanner: () => null,
              renderFooter: () => null,
              renderHeader: () => null,
            })}
            trackEvent={(eventName: string, options = {}) => {
              emitEvent(eventName as EventNameType, {
                ...options,
              });
            }}
          >
            {children}
            <FullStory user={user} />
          </UniversalLayout>
        </SplitFactoryProvider>
      </TrackRouting>
    </CurrentAccountIdProvider>
  );
}

export default Sentry.withProfiler(AppLayout);
